/**
 * Tile registry for the Stammdaten cockpit (issue #1064), read from the served
 * menu definition (BLP/nx#3257): `menu.cockpit` holds the domains and their
 * subareas in display order, the tile classes and the tiles in display order.
 *
 * The Stammdaten nav in `connect/src/integration/erp.ts` is the palette's view of
 * the same tiles; `tiles.test.ts` fails when a generic resource has no tile.
 *
 * Nothing here holds user-facing text: domains, subareas, classes and tiles carry
 * i18n keys, the texts are served with the definitions. Every reader is a
 * function — the definitions are not there when the shell's eager slice
 * (`tileTargets.ts`) evaluates this module.
 */
import { erpMenuDefinition } from '@/definitions/store';
import type { ErpCockpit, ErpCockpitTile } from '@/definitions/types';

/** A cockpit domain id (`central`, `administration`, …). */
export type CockpitDomain = string;
/** A tile class id (`A`, `B`, `C`), see `classLabelKey`. */
export type CockpitClass = string;

/**
 * A tile: stable id (the DOM key and the last segment of `labelKey`), the
 * module class (SDR-BR-004; the filter chips select on it), the domain and
 * subarea it sits under and the target route name (unprefixed; Connect adds
 * `erp.`).
 */
export type CockpitTile = ErpCockpitTile;

const cockpit = (): ErpCockpit => erpMenuDefinition().cockpit;

/** Page heading key. */
export const cockpitTitleKey = (): string => cockpit().titleKey;

/** The cockpit domains, in display order. */
export const cockpitDomains = (): readonly CockpitDomain[] =>
  cockpit().domains.map((domain) => domain.id);

/** The ids of a domain's subareas, in display order. */
export const cockpitSubareas = (domain: CockpitDomain): readonly string[] =>
  cockpit()
    .domains.find((candidate) => candidate.id === domain)
    ?.subareas.map((subarea) => subarea.id) ?? [];

/** The icon name of a domain (`databaseMultiple`, …); the view maps it to a component. */
export const domainIconName = (domain: CockpitDomain): string | undefined =>
  cockpit().domains.find((candidate) => candidate.id === domain)?.icon;

/**
 * Module classes from #1044, which decide how a module is built:
 *
 * - **A — Listen-Stammdaten:** one section, one standard table, maintained in the
 *   list itself.
 * - **B — Kopf-Stammdaten:** one record with several field sections.
 * - **C — Master-Detail:** a head record with several sections *including detail
 *   tables* (positions, protocols, B-DMS, activities).
 *
 * The dividing line between B and C is the detail table, not the section count.
 */
export const cockpitClasses = (): readonly CockpitClass[] =>
  cockpit().classes.map((tileClass) => tileClass.id);

/** Every tile, in display order. */
export const cockpitTiles = (): readonly CockpitTile[] => cockpit().tiles;

/**
 * Caption keys of the domains, subareas and classes, as served. One key per
 * class, shared by the filter chip and the tile badge: both name the same class,
 * and two keys holding "Klasse A" would drift the moment one is renamed. The
 * badge shows the bare letter and carries this as its title.
 */
export const domainLabelKey = (domain: CockpitDomain): string =>
  cockpit().domains.find((candidate) => candidate.id === domain)?.labelKey ??
  `masterdata.cockpit.domains.${domain}`;

export const subareaLabelKey = (domain: CockpitDomain, subarea: string): string =>
  cockpit()
    .domains.find((candidate) => candidate.id === domain)
    ?.subareas.find((candidate) => candidate.id === subarea)?.labelKey ??
  `masterdata.cockpit.subareas.${subarea}`;

export const classLabelKey = (tileClass: CockpitClass): string =>
  cockpit().classes.find((candidate) => candidate.id === tileClass)?.labelKey ??
  `masterdata.cockpit.classes.${tileClass}`;
